import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Users,
  Wallet,
  Handshake,
  FileText,
  Image,
  Newspaper,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Shield,
  Briefcase,
  Calendar,
  Megaphone,
  Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const AdminLayout = () => {
  const { user, logout, hasPermission, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin',
      permission: null
    },
    {
      label: 'Portfolio',
      icon: Building2,
      path: '/admin/portfolio',
      permission: 'portfolio'
    },
    {
      label: 'Team',
      icon: Users,
      path: '/admin/team',
      permission: 'team'
    },
    {
      label: 'Funds',
      icon: Briefcase,
      path: '/admin/funds',
      permission: 'funds'
    },
    {
      label: 'Investors',
      icon: Wallet,
      path: '/admin/investors',
      permission: 'investors'
    },
    {
      label: 'Partners',
      icon: Handshake,
      path: '/admin/partners',
      permission: 'partners'
    },
    {
      label: 'Blog',
      icon: FileText,
      path: '/admin/blog',
      permission: 'blog'
    },
    {
      label: 'Media',
      icon: Image,
      path: '/admin/media',
      permission: 'media'
    },
    {
      label: 'News',
      icon: Newspaper,
      path: '/admin/news',
      permission: 'news'
    },
    {
      label: 'Events',
      icon: Calendar,
      path: '/admin/events',
      permission: 'news'
    },
    {
      label: 'Resources',
      icon: FileText,
      path: '/admin/resources',
      permission: 'news'
    },
    {
      label: 'Site Banner',
      icon: Megaphone,
      path: '/admin/site-banner',
      permission: 'news'
    },
    {
      label: 'Subscribers',
      icon: Mail,
      path: '/admin/newsletter-subscribers',
      permission: 'news'
    }
  ];

  const filteredNavItems = navItems.filter(item => 
    item.permission === null || hasPermission(item.permission)
  );

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.path}
      end={item.path === '/admin'}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? 'bg-[#C8473A] text-white'
            : 'text-gray-700 hover:bg-gray-100'
        }`
      }
    >
      <item.icon className="w-5 h-5" />
      <span className={sidebarOpen || mobile ? 'block' : 'hidden'}>{item.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img
          src="/images/yournest-logo-admin.png"
          alt="YourNest"
          className="h-8 object-contain"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2">
              <img
                src={user?.picture || `https://ui-avatars.com/api/?name=${user?.name}`}
                alt={user?.name}
                className="w-8 h-8 rounded-full"
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <nav
            className="absolute left-0 top-16 bottom-0 w-64 bg-white overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 space-y-1">
              {filteredNavItems.map(item => (
                <NavItem key={item.path} item={item} mobile />
              ))}
              {isSuperAdmin() && (
                <>
                  <div className="border-t my-4" />
                  <NavLink
                    to="/admin/users"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-[#C8473A] text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                  >
                    <Shield className="w-5 h-5" />
                    <span>User Management</span>
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-0 bottom-0 bg-white border-r transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b">
          {sidebarOpen && (
            <img
              src="/images/yournest-logo-admin.png"
              alt="YourNest"
              className="h-8 object-contain"
            />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {filteredNavItems.map(item => (
            <NavItem key={item.path} item={item} />
          ))}
          
          {isSuperAdmin() && (
            <>
              <div className="border-t my-4" />
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#C8473A] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <Shield className="w-5 h-5" />
                <span className={sidebarOpen ? 'block' : 'hidden'}>User Management</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-3 w-full p-2 rounded-lg hover:bg-gray-100 ${sidebarOpen ? '' : 'justify-center'}`}>
                <img
                  src={user?.picture || `https://ui-avatars.com/api/?name=${user?.name}`}
                  alt={user?.name}
                  className="w-10 h-10 rounded-full"
                />
                {sidebarOpen && (
                  <div className="flex-1 text-left">
                    <p className="font-medium text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                )}
                {sidebarOpen && <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-16 lg:pt-0 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
